import {
  doc,
  getDoc,
  getDocFromServer,
  setDoc,
  updateDoc,
  deleteDoc,
  onSnapshot,
  serverTimestamp,
  collection,
  query,
  where,
  orderBy,
  limit,
  getDocs,
} from "firebase/firestore";
import { db } from "../firebase/config";

const COLLECTION = "matches";

function matchRef(matchId) {
  return doc(db, COLLECTION, matchId);
}

export async function fetchMatch(matchId) {
  const snap = await getDoc(matchRef(matchId));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

/**
 * Bypass local IndexedDB cache — use after create so invite links are not shared
 * before remote peers can read the arena.
 */
export async function fetchMatchFromServer(matchId) {
  if (!matchId) return null;
  const snap = await getDocFromServer(matchRef(matchId));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

export async function createMatch(matchId, data) {
  await setDoc(matchRef(matchId), data);
}

export async function updateMatch(matchId, data) {
  await updateDoc(matchRef(matchId), data);
}

export async function deleteMatch(matchId) {
  await deleteDoc(matchRef(matchId));
}

/** Rooms created by this user (includes archived; filter in service layer). */
export async function listMatchesCreatedBy(uid, max = 30) {
  if (!uid) return [];
  const q = query(
    collection(db, COLLECTION),
    where("createdBy", "==", uid),
    orderBy("createdAt", "desc"),
    limit(max)
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

/**
 * @typedef {{ fromCache: boolean, hasPendingWrites: boolean }} SnapshotMeta
 */

/** @returns {import('firebase/firestore').Unsubscribe} */
export function subscribeMatch(matchId, onData) {
  return onSnapshot(
    matchRef(matchId),
    (snap) => {
      const meta = {
        fromCache: snap.metadata.fromCache,
        hasPendingWrites: snap.metadata.hasPendingWrites,
      };
      if (snap.exists()) onData({ id: snap.id, ...snap.data() }, meta);
      else onData(null, meta);
    },
    { includeMetadataChanges: true }
  );
}

export function newMatchTimestamps() {
  const now = serverTimestamp();
  return { createdAt: now, updatedAt: now };
}

export { COLLECTION as MATCHES_COLLECTION };
